<script lang="ts">
	import type { OrderConfigurationOrder } from '$models/OrderConfigurator/OrderConfigurator';
	import type { OrderConfiguratorShop } from '$models/OrderConfigurator/OrderConfiguratorShop';
	import { page } from '$app/state';
	import PriceScaleMessage from '$components/PriceScale/PriceScaleMessage.svelte';
	import QuantityValidationMessage from '$components/Quantity/QuantityValidationMessage.svelte';
	import VatLabel from '$components/Shop/VatLabel.svelte';
	import CartIcon from '$components/UI/Icons/CartIcon.svelte';
	import { getSiteContext } from '$lib/context/site';
	import { getWordPressImageSrcSet } from '$lib/images';
	import { moneyForDisplay } from '$lib/money';
	import { getShopPresentation } from '$lib/shop/presentation.svelte';
	import {
		getConfiguratorShopItemPrice,
		getConfiguratorState
	} from '$lib/state/configurator.svelte';
	import { isRegularProduct } from '$lib/type-helpers';
	import { formatCurrency } from '$lib/utils';

	interface Props {
		configurator: OrderConfiguratorShop;
		onSubmit: (order: OrderConfigurationOrder) => void;
	}

	let { configurator, onSubmit }: Props = $props();

	const configuratorState = getConfiguratorState();
	const presentation = getShopPresentation();
	const site = getSiteContext();
	const translations = page.data.page.layout.translations;

	let quantityMultiplier = $derived(configuratorState.quantityMultiplier());
	let selectedCategory = $derived(configuratorState.selectedCategory());
	let selectedQuantity = $derived(configuratorState.selectedQuantity());
	let selectedDisplayQuantity = $derived(selectedQuantity * quantityMultiplier);
	let products = $derived(
		configuratorState.products().length > 0
			? configuratorState.products()
			: configuratorState.nonSubscribableProducts()
	);
	let canAddToCart = $derived.by(() => {
		if (!configuratorState.canSubmitPricing()) return false;
		if (configuratorState.products().length > 1) {
			return configuratorState.isValid();
		}

		return (
			configuratorState.selectedQuantity() > 0 ||
			configuratorState.selectedNonSubscribableProductsQuantity() > 0
		);
	});
	let totalPrice = $derived.by(() => {
		if (configuratorState.usesTotalPrice) {
			const quote = configuratorState.quote();
			const total =
				presentation.ready && quote?.payable
					? moneyForDisplay(quote.beforeDiscount, presentation.vat)
					: null;
			const referenceQuantity = configuratorState.fromPriceQuantity();
			return total !== null && referenceQuantity && configuratorState.products().length === 1
				? (total * selectedQuantity) / referenceQuantity
				: total;
		}
		if (configuratorState.isValid()) {
			return configuratorState.total();
		}

		if (configuratorState.products().length === 1) {
			return getConfiguratorShopItemPrice(configuratorState) * configuratorState.selectedQuantity();
		}

		if (configuratorState.products().length === 0) {
			return configuratorState.baseTotal();
		}

		return null;
	});
	let discount = $derived.by(() => {
		if (!configuratorState.usesTotalPrice) return configuratorState.discount();
		const quote = configuratorState.quote();
		const amount =
			quote?.status === 'applied' ? moneyForDisplay(quote.discount, presentation.vat) : 0;
		const referenceQuantity = configuratorState.fromPriceQuantity();
		return referenceQuantity && configuratorState.products().length === 1
			? (amount * selectedQuantity) / referenceQuantity
			: amount;
	});

	function handleSubmit() {
		if (!canAddToCart) return;
		onSubmit(configuratorState.order());
	}
</script>

<div class="shop-configurator">
	<div class="shop-configurator-inner">
		{#if configurator.productSelectionTitle || configurator.productSelectionSubtitle}
			<div class="shop-configurator-header">
				<h3 class="shop-configurator-title">{configurator.productSelectionTitle}</h3>
				{#if configurator.productSelectionSubtitle}
					<p class="shop-configurator-subtitle">{configurator.productSelectionSubtitle}</p>
				{/if}
			</div>
		{/if}
		<div class="shop-configurator-products">
			{#each products as { product, quantity }, i}
				{@const quantityMultiplier = isRegularProduct(product)
					? (product.quantityMultiplier ?? 1)
					: 1}
				{@const displayQuantity = quantity * quantityMultiplier}
				<div class="shop-configurator-product">
					<figure class="shop-configurator-product-image">
						{#if product.image}
							<img
								src={product.image.sizes.thumbnail}
								srcset={getWordPressImageSrcSet(product.image)}
								sizes="36px"
								alt={product.name}
								width={product.image.width}
								height={product.image.height}
								loading="lazy"
								decoding="async"
							/>
						{/if}
					</figure>
					<div class="shop-configurator-product-info">
						<div class="shop-configurator-product-name">
							{product.name}
						</div>
					</div>
					<div class="shop-configurator-product-input">
						<button
							class="shop-configurator-product-quantity-button shop-configurator-product-quantity-button--minus"
							onclick={() => configuratorState.decrementProductQuantity(product)}
						>
							<span class="screenreader">-</span>
						</button>

						<input
							class="shop-configurator-product-quantity"
							type="text"
							value={displayQuantity}
							onchange={(e) =>
								configuratorState.setProductQuantity(
									product,
									parseInt(e.target.value) / quantityMultiplier
								)}
						/>
						<button
							class="shop-configurator-product-quantity-button shop-configurator-product-quantity-button--plus"
							onclick={() => configuratorState.incrementProductQuantity(product)}
						>
							<span class="screenreader">+</span>
						</button>
					</div>
				</div>
			{/each}
		</div>
		<!-- Does this make sense at the moment? Not for non-subscribable products at least (always 0)
		<div class="shop-configurator-totals">
			{selectedDisplayQuantity}
			{#if selectedDisplayQuantity === 1}
				{selectedCategory?.translations.unitNameSingle}
			{:else}
				{selectedCategory?.translations.unitNamePlural}
			{/if}
		</div> -->
		<div class="shop-configurator-footer">
			<!-- Disable until we decide if/what to show
			{#if site.showUnitPrices && selectedCategory && totalPrice > 0}
				<div class="shop-configurator-unit-price">
					{formatCurrency(
						(totalPrice - configuratorState.discount()) / selectedDisplayQuantity,
						site.locale.code
					)}
					{translations.perUnitText.replaceAll(
						'%product%',
						selectedCategory.translations.unitNameSingle
					)}
				</div>
			{/if} -->
			<div class="shop-configurator-price">
				{#if totalPrice !== null && totalPrice >= 0}
					{#if configuratorState.usesTotalPrice && configuratorState.fromPriceQuantity()}
						<span>{translations.fromPriceLabel}</span>
					{/if}
					{#if discount > 0}
						<!-- Regular price -->
						<span class="shop-configurator-price-old">
							{formatCurrency(totalPrice, site.locale.code)}
						</span>
						<!-- Discounted price -->
						{formatCurrency(totalPrice - discount, site.locale.code)}
					{:else}
						<!-- Regular price -->
						{formatCurrency(totalPrice, site.locale.code)}
					{/if}
				{/if}
				<VatLabel />
			</div>
			<button
				class="shop-configurator-button button"
				disabled={!canAddToCart}
				onclick={handleSubmit}
			>
				<CartIcon />
				{configurator.orderButtonText}
			</button>
		</div>
	</div>
	<!-- Validation and upsell disabled for now. Anything can be added to cart, validation is done there.
	{#if !configuratorState.isValidQuantity()}
		<div class="shop-configurator-message">
			<svg
				class="shop-configurator-message-icon"
				width="18"
				height="18"
				viewBox="0 0 18 18"
				fill="none"
				xmlns="http://www.w3.org/2000/svg"
			>
				<path
					d="M9 0C7.21997 0 5.47991 0.527841 3.99987 1.51677C2.51983 2.50571 1.36628 3.91131 0.685088 5.55585C0.00389956 7.20038 -0.17433 9.00998 0.172937 10.7558C0.520203 12.5016 1.37737 14.1053 2.63604 15.364C3.89471 16.6226 5.49836 17.4798 7.24419 17.8271C8.99002 18.1743 10.7996 17.9961 12.4442 17.3149C14.0887 16.6337 15.4943 15.4802 16.4832 14.0001C17.4722 12.5201 18 10.78 18 9C17.9975 6.61382 17.0485 4.3261 15.3612 2.63882C13.6739 0.95154 11.3862 0.00251984 9 0ZM9 16.6154C7.49382 16.6154 6.02146 16.1687 4.76912 15.332C3.51678 14.4952 2.5407 13.3058 1.96431 11.9143C1.38792 10.5228 1.23711 8.99155 1.53095 7.51431C1.82479 6.03707 2.55008 4.68014 3.61511 3.61511C4.68014 2.55008 6.03708 1.82478 7.51431 1.53094C8.99155 1.2371 10.5228 1.38791 11.9143 1.9643C13.3058 2.54069 14.4952 3.51677 15.332 4.76912C16.1688 6.02146 16.6154 7.49382 16.6154 9C16.6131 11.019 15.81 12.9547 14.3824 14.3824C12.9547 15.81 11.019 16.6131 9 16.6154ZM8.30769 9.69231V4.84615C8.30769 4.66254 8.38063 4.48645 8.51047 4.35662C8.6403 4.22678 8.81639 4.15385 9 4.15385C9.18361 4.15385 9.3597 4.22678 9.48954 4.35662C9.61937 4.48645 9.69231 4.66254 9.69231 4.84615V9.69231C9.69231 9.87592 9.61937 10.052 9.48954 10.1818C9.3597 10.3117 9.18361 10.3846 9 10.3846C8.81639 10.3846 8.6403 10.3117 8.51047 10.1818C8.38063 10.052 8.30769 9.87592 8.30769 9.69231ZM10.0385 12.8077C10.0385 13.0131 9.97756 13.2139 9.86345 13.3846C9.74934 13.5554 9.58716 13.6885 9.3974 13.7671C9.20765 13.8457 8.99885 13.8663 8.79741 13.8262C8.59597 13.7861 8.41093 13.6872 8.2657 13.542C8.12047 13.3968 8.02156 13.2117 7.98149 13.0103C7.94142 12.8088 7.96199 12.6 8.04059 12.4103C8.11919 12.2205 8.25229 12.0583 8.42306 11.9442C8.59384 11.8301 8.79461 11.7692 9 11.7692C9.27542 11.7692 9.53956 11.8786 9.7343 12.0734C9.92905 12.2681 10.0385 12.5323 10.0385 12.8077Z"
					fill="currentColor"
				/>
			</svg>
			<span class="shop-configurator-message-text">
				<QuantityValidationMessage
					quantities={configuratorState.quantities()}
					quantity={configuratorState.selectedQuantity()}
					multiplier={configuratorState.quantityMultiplier()}
					{...selectedCategory?.translations}
				/>
			</span>
		</div>
	{:else}
		<PriceScaleMessage
			quantity={selectedDisplayQuantity}
			priceScales={selectedCategory?.priceScales}
			{...selectedCategory?.translations}
		/>
	{/if} -->
</div>

<style lang="postcss">
	.shop-configurator {
		& {
			border: 1px solid var(--color-primary);
			border-radius: var(--radius-4);
			overflow: hidden;
		}
		&-inner {
			padding: var(--spacing-5);
		}
		&-header {
			margin-bottom: var(--spacing-4);
		}
		&-title {
			font-size: var(--font-size-2);
			line-height: var(--font-lineheight-0);

			&:not(:last-child) {
				margin-bottom: var(--spacing-1);
			}
		}
		&-subtitle {
			color: var(--color-grey);
			font-size: var(--font-size-1);
		}
		&-products {
			margin-bottom: var(--spacing-6);
		}
		&-product {
			display: flex;
			align-items: center;

			&:not(:last-child) {
				margin-bottom: 10px;
			}

			&-image {
				width: 100%;
				max-width: 36px;
				aspect-ratio: 48 / 42;
				margin-right: var(--spacing-2);

				img {
					display: block;
					width: 100%;
					height: 100%;
					object-fit: cover;
				}
			}

			&-info {
				padding-right: var(--spacing-3);
				flex: 1;
				font-size: 14px;
				line-height: var(--font-lineheight-0);
				color: var(--title-color);
				overflow: hidden;

				@media (--xs) {
					font-size: 15px;
				}
			}

			&-name {
				font-family: var(--title-font-family);
				font-weight: var(--title-font-weight);
				text-transform: var(--box-title-text-transform);
				white-space: nowrap;
				overflow: hidden;
				text-overflow: ellipsis;

				&:not(:last-child) {
					margin-bottom: 4px;
				}
			}

			&-input {
				display: flex;
				align-items: center;
				border: 1px solid var(--border-color);
				padding-left: 4px;
				padding-right: 4px;
				border-radius: 18px;
			}

			&-quantity {
				text-align: center;
				border: 0;
				line-height: 1.2;
				padding: 10px 0;
				max-width: 36px;
				max-height: 34px;

				&-button {
					display: flex;
					align-items: center;
					justify-content: center;
					width: 24px;
					height: 24px;
					color: var(--color-primary);

					&:before,
					&:after {
						content: '';
						position: absolute;
						width: 10px;
						height: 2px;
						border-radius: 1px;
						background: var(--color-primary);
					}

					&:after {
						transform: rotate(90deg);
					}

					&--minus {
						&:after {
							display: none;
						}
					}

					&--trash {
						&:after,
						&:before {
							display: none;
						}
					}
				}
			}
		}
		&-totals {
			display: flex;
			flex-direction: column;
			align-items: flex-end;
			margin-bottom: var(--spacing-4);

			&:before {
				content: '';
				display: block;
				width: 64px;
				height: 2px;
				border-radius: 1px;
				background: var(--color-primary);
				margin-bottom: var(--spacing-4);
			}
		}
		&-footer {
			display: flex;
			flex-wrap: wrap;
			align-items: center;
			justify-content: flex-end;
			gap: var(--spacing-5);

			@media (--lg) {
				gap: var(--spacing-4);
			}
		}
		/* &-unit-price {
			margin-right: auto;
			font-size: var(--font-size-0);
			color: var(--color-grey-2);
		} */
		&-price {
			display: flex;
			align-items: center;
			gap: 5px;
			font-size: var(--font-size-2);
			line-height: var(--font-lineheight-0);
			letter-spacing: -0.35px;

			@media (--lg) {
				font-size: 19px;
			}
		}
		&-price-old {
			display: inline-block;
			font-size: var(--font-size-0);
			color: var(--color-grey);
			text-decoration: line-through;
			text-decoration-thickness: 1px;
			margin-right: var(--spacing-2);
		}
		&-button {
			display: flex;
			align-items: center;
			gap: var(--spacing-2);
			padding-left: 18px;
			padding-right: 18px;
			width: 100%;
			text-align: center;
			justify-content: center;

			@media (--xs) {
				width: auto;
			}

			@media (--md) {
				width: 100%;
			}

			@media (--lg) {
				width: auto;
			}

			&:after {
				display: none;
			}
		}
		&-message {
			display: flex;
			align-items: center;
			gap: var(--spacing-2);
			font-size: 14px;
			line-height: var(--font-lineheight-0);
			padding: var(--spacing-3);
			background-color: var(--color-secondary);

			@media (--sm) {
				font-size: var(--font-size-0);
				padding: var(--spacing-4);
			}
		}
	}
</style>
